import React from 'react';
import { useSelector } from 'react-redux';
import { Button, Modal, Tag } from 'antd';
import { RootState } from '../../store/store';
import { AttachToOrderContext } from '../../modules/ShoppingCart/ShoppingCartSliceV2';

interface Props {
  open: boolean;
  onContinue: () => void;
  onCancelMode: () => void;
  // context to display — the pending (not-yet-applied) attach context, so the
  // modal can render while redux is still in regular mode
  context?: AttachToOrderContext | null;
}

/**
 * Shown once per tab when the "add/replace product on an existing
 * order" mode is silently resumed from localStorage (reload / new tab), so the
 * agent can't accidentally keep replacing products in a mode they forgot
 * about. Deliberately NOT dismissable (no X / mask / Esc) — the agent must
 * explicitly continue or cancel the mode.
 */
const OrderAttachConfirmModal = ({
  open,
  onContinue,
  onCancelMode,
  context,
}: Props) => {
  const { shoppingCartTranslations: t } = useSelector(
    (state: RootState) => state.config.translations
  );

  if (!context) {
    return null;
  }

  const attachToOrder = context;
  const isReplace = attachToOrder.mode === 'replace';

  return (
    <Modal
      open={open}
      title={t?.attachConfirmTitle}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={[
        <Button key="cancel" onClick={onCancelMode}>
          {t?.attachConfirmCancelMode}
        </Button>,
        <Button key="continue" type="primary" onClick={onContinue}>
          {t?.attachConfirmContinue}
        </Button>,
      ]}
    >
      <p>
        <Tag color={isReplace ? 'orange' : 'green'}>
          {isReplace ? t?.attachModeReplace : t?.attachModeAdd}
        </Tag>
      </p>
      <p>
        {isReplace
          ? t?.attachConfirmReplaceExplanation
          : t?.attachConfirmAddExplanation}
      </p>
      <p>
        {isReplace
          ? `${t?.attachReplacingFor}${
              attachToOrder.replaced_product_name
                ? ` "${attachToOrder.replaced_product_name}"`
                : ''
            } ${t?.attachOnOrder} #${attachToOrder.order_id}`
          : `${t?.attachAddingTo} #${attachToOrder.order_id}`}
        {attachToOrder.customer_name
          ? ` — ${t?.attachClient}: ${attachToOrder.customer_name}`
          : ''}
      </p>
      <p>{t?.attachConfirmQuestion}</p>
    </Modal>
  );
};

export default OrderAttachConfirmModal;
